/**
 * @description
 * Security settings component for marketplace configuration.
 * Handles JWT secret templates and security configurations.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 */

import { useState } from 'react';
import { SecuritySettingsProps } from './types';
import { useMarketplaceConfig } from './hooks/useMarketplaceConfig';

/**
 * Security settings component
 */
export function SecuritySettings({ onRefresh }: SecuritySettingsProps) {
  const { config, updateConfig } = useMarketplaceConfig();
  const [formData, setFormData] = useState({
    jwtSecretTemplate: config?.jwtSecretTemplate || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await updateConfig({ jwtSecretTemplate: formData.jwtSecretTemplate });
      await onRefresh();
    } catch (error) {
      console.error('Error updating security settings:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateRandomSecret = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 64; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, jwtSecretTemplate: result }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Security Settings</h2>
        <p className="text-sm text-muted-foreground">
          Configure JWT secret templates and security settings for marketplace apps.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="jwtSecretTemplate" className="block text-sm font-medium mb-2">
            JWT Secret Template
          </label>
          <div className="flex gap-2">
            <input
              id="jwtSecretTemplate"
              type="password"
              value={formData.jwtSecretTemplate}
              onChange={(e) => handleInputChange('jwtSecretTemplate', e.target.value)}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder="Enter JWT secret template (minimum 32 characters)"
              required
              minLength={32}
            />
            <button
              type="button"
              onClick={generateRandomSecret}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
            >
              Generate
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            This template will be used to generate unique JWT secrets for each marketplace app deployment.
            Must be at least 32 characters long.
          </p>
        </div>

        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Security Notice
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  The JWT secret template is used to generate unique secrets for each marketplace app.
                  Keep this template secure and never share it publicly.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
          >
            {isSubmitting ? 'Saving...' : 'Save Security Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}